import { FamilySiteLogoPicker } from '@/components/admin/FamilySiteLogoPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type {
  AdminFamilySite,
  CreateFamilySiteRequest,
  FamilySiteLogoType,
  UpdateFamilySiteRequest,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';

interface AdminFamilySiteDetailProps {
  id: string;
}

interface FormState {
  name: string;
  url: string;
  logoType: FamilySiteLogoType;
  logoEmoji: string;
  logoLucideName: string;
  openInNewTab: boolean;
}

const EMPTY_STATE: FormState = {
  name: '',
  url: '',
  logoType: 'emoji',
  logoEmoji: '🏢',
  logoLucideName: '',
  openInNewTab: true,
};

function siteToForm(site: AdminFamilySite): FormState {
  return {
    name: site.name,
    url: site.url,
    logoType: site.logoType,
    logoEmoji: site.logoEmoji ?? '',
    logoLucideName: site.logoLucideName ?? '',
    openInNewTab: site.openInNewTab,
  };
}

export function AdminFamilySiteDetail({ id }: AdminFamilySiteDetailProps) {
  const isNew = id === 'new';
  return isNew ? <CreateView /> : <EditView id={id} />;
}

function BackLink() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/admin/family-sites' })}
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      패밀리 사이트 목록
    </button>
  );
}

function CreateView() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateFamilySiteRequest) => api.adminFamilySiteCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites'] });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
      navigate({ to: '/admin/family-sites' });
    },
  });

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? '사이트 생성에 실패했습니다.'
        : null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) return setFieldError('이름을 입력해 주세요.');
    if (!url) return setFieldError('URL 을 입력해 주세요.');
    if (form.logoType === 'emoji' && !form.logoEmoji.trim()) {
      return setFieldError('이모지를 입력해 주세요.');
    }
    if (form.logoType === 'lucide' && !form.logoLucideName.trim()) {
      return setFieldError('아이콘을 선택해 주세요.');
    }
    const body: CreateFamilySiteRequest = {
      name,
      url,
      openInNewTab: form.openInNewTab,
      logoType: form.logoType,
      logoEmoji: form.logoType === 'emoji' ? form.logoEmoji.trim() : null,
      logoLucideName: form.logoType === 'lucide' ? form.logoLucideName.trim() : null,
    };
    mutation.mutate(body);
  }

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title="새 패밀리 사이트"
            description="이름·URL·로고를 등록하면 사용자 사이드바에 표시됩니다."
          />
        </>
      }
    >
      <FormFields
        form={form}
        setForm={setForm}
        fieldError={fieldError}
        serverError={serverError}
        onSubmit={onSubmit}
        submitting={mutation.isPending}
        submitLabel="만들기"
        onCancel={() => navigate({ to: '/admin/family-sites' })}
        imageDisabledReason="이미지 업로드는 먼저 사이트를 저장한 뒤 편집 화면에서 진행해 주세요."
      />
    </PageShell>
  );
}

function EditView({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'family-sites', id],
    queryFn: () =>
      api.adminFamilySiteList().then((list) => list.sites.find((s) => s.id === id) ?? null),
  });

  useEffect(() => {
    if (query.data && !form) setForm(siteToForm(query.data));
  }, [query.data, form]);

  const updateMutation = useMutation({
    mutationFn: (body: UpdateFamilySiteRequest) => api.adminFamilySiteUpdate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites'] });
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites', id] });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
      navigate({ to: '/admin/family-sites' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.adminFamilySiteDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites'] });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
      navigate({ to: '/admin/family-sites' });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.adminFamilySiteUploadLogo(id, file),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites'] });
      qc.invalidateQueries({ queryKey: ['admin', 'family-sites', id] });
      qc.invalidateQueries({ queryKey: ['family-sites'] });
      // 폼 상태도 새 image 로 갱신.
      setForm(siteToForm(updated));
    },
  });

  if (query.isLoading || !form) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (!query.data) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">사이트를 찾을 수 없습니다.</p>
      </PageShell>
    );
  }

  const serverError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : uploadMutation.error instanceof ApiError
        ? uploadMutation.error.message
        : deleteMutation.error instanceof ApiError
          ? deleteMutation.error.message
          : null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    if (!form) return;
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) return setFieldError('이름을 입력해 주세요.');
    if (!url) return setFieldError('URL 을 입력해 주세요.');
    if (form.logoType === 'emoji' && !form.logoEmoji.trim()) {
      return setFieldError('이모지를 입력해 주세요.');
    }
    if (form.logoType === 'lucide' && !form.logoLucideName.trim()) {
      return setFieldError('아이콘을 선택해 주세요.');
    }
    const body: UpdateFamilySiteRequest = {
      name,
      url,
      openInNewTab: form.openInNewTab,
      logoType: form.logoType,
      logoEmoji: form.logoType === 'emoji' ? form.logoEmoji.trim() : null,
      logoLucideName: form.logoType === 'lucide' ? form.logoLucideName.trim() : null,
    };
    updateMutation.mutate(body);
  }

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={query.data.name}
            description="이름·URL·로고를 수정할 수 있습니다."
            actions={
              <Button
                variant="ghost"
                onClick={() => {
                  if (window.confirm(`"${query.data?.name}" 을 삭제할까요?`)) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? '삭제 중…' : '삭제'}
              </Button>
            }
          />
        </>
      }
    >
      <FormFields
        form={form}
        setForm={setForm as (next: FormState) => void}
        fieldError={fieldError}
        serverError={serverError}
        onSubmit={onSubmit}
        submitting={updateMutation.isPending}
        submitLabel="저장"
        onCancel={() => navigate({ to: '/admin/family-sites' })}
        imageUrl={query.data.logoType === 'image' ? query.data.logoUrl : null}
        onPickFile={(file) => uploadMutation.mutate(file)}
        imagePending={uploadMutation.isPending}
      />
    </PageShell>
  );
}

interface FormFieldsProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>> | ((next: FormState) => void);
  fieldError: string | null;
  serverError: string | null;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  imageUrl?: string | null;
  onPickFile?: (file: File) => void;
  imagePending?: boolean;
  imageDisabledReason?: string;
}

function FormFields({
  form,
  setForm,
  fieldError,
  serverError,
  submitting,
  submitLabel,
  onSubmit,
  onCancel,
  imageUrl,
  onPickFile,
  imagePending,
  imageDisabledReason,
}: FormFieldsProps) {
  const update = (patch: Partial<FormState>) => {
    (setForm as React.Dispatch<React.SetStateAction<FormState>>)((prev) => ({ ...prev, ...patch }));
  };

  return (
    <form onSubmit={onSubmit} className="max-w-2xl space-y-6">
      <div className="bg-surface border border-border rounded-2xl p-6 space-y-4">
        <Field label="이름" htmlFor="fs-name" hint="사이드바에 표시될 사이트 이름.">
          <Input
            id="fs-name"
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="예: 회사 홈페이지"
            maxLength={60}
          />
        </Field>
        <Field label="URL" htmlFor="fs-url" hint="https:// 로 시작하는 전체 주소.">
          <Input
            id="fs-url"
            value={form.url}
            onChange={(e) => update({ url: e.target.value })}
            placeholder="https://example.com"
            type="url"
            maxLength={500}
          />
        </Field>

        <div className="space-y-2">
          <span className="text-sm font-medium">로고</span>
          <FamilySiteLogoPicker
            logoType={form.logoType}
            logoEmoji={form.logoEmoji || null}
            logoLucideName={form.logoLucideName || null}
            imageUrl={imageUrl ?? null}
            onChangeType={(t) => update({ logoType: t })}
            onChangeEmoji={(v) => update({ logoEmoji: v })}
            onChangeLucide={(n) => update({ logoLucideName: n })}
            onPickFile={onPickFile}
            imagePending={imagePending}
            imageDisabledReason={
              form.logoType === 'image' && !onPickFile ? imageDisabledReason : undefined
            }
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.openInNewTab}
            onChange={(e) => update({ openInNewTab: e.target.checked })}
            className="size-4"
          />
          <span>새 탭에서 열기</span>
          <span className="text-xs text-foreground-muted">
            (체크 해제 시 현재 탭에서 페이지 이동)
          </span>
        </label>
      </div>

      {fieldError ? <p className="text-sm text-destructive">{fieldError}</p> : null}
      {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? '저장 중…' : submitLabel}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          취소
        </Button>
      </div>
    </form>
  );
}
